import React, { useState } from 'react';
import RichTextEditor from '../../../components/RichTextEditor';
import LookupField from '../../../components/lookup/LookupField';
import UserLookupModal from '../../../components/lookup/UserLookupModal';
import GroupLookupModal from '../../../components/lookup/GroupLookupModal';
import { CAB_BOARD_GROUP_TYPES, CAB_CONFERENCE_TYPE_OPTIONS, CAB_TYPE_FORM_OPTIONS } from '../../../constants/cabDefinitionOptions';
import type { CabDefinitionFormData } from '../../../types/cabDefinition';
import type { CabDefinitionFormErrors } from '../../../utils/cabDefinitionValidation';
import { formatGroupDisplayName, formatUserDisplayName } from '../../../components/lookup/lookupUtils';
import type { LookupGroup, LookupUser } from '../../../components/lookup/lookupTypes';

type LookupTarget = 'cabManager' | 'delegates' | 'boardMembers' | 'boardGroups' | null;

interface CabDefinitionFormProps {
  formData: CabDefinitionFormData;
  errors: CabDefinitionFormErrors;
  onChange: (updates: Partial<CabDefinitionFormData>) => void;
  onSubmit: (action: 'save' | 'saveContinue') => void;
  onCancel: () => void;
  isSaving?: boolean;
  nameReadOnly?: boolean;
  showFooter?: boolean;
}

function FormTextField({
  id,
  label,
  required,
  value,
  onChange,
  error,
  type = 'text',
  maxLength,
  readOnly = false,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string | number;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  maxLength?: number;
  readOnly?: boolean;
}) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {required && <span className="text-red-500 mr-0.5" aria-hidden>*</span>}
          {label}
        </label>
      </div>
      <div className="col-span-12 sm:col-span-8 lg:col-span-9">
        <input
          id={id}
          type={type}
          value={value}
          maxLength={maxLength}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => onChange(e.target.value)}
          className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
            error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
          } ${readOnly ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''}`}
        />
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}

function FormSelectField({
  id,
  label,
  required,
  value,
  onChange,
  options,
  error,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  error?: string;
}) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {required && <span className="text-red-500 mr-0.5" aria-hidden>*</span>}
          {label}
        </label>
      </div>
      <div className="col-span-12 sm:col-span-8 lg:col-span-9">
        <select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
            error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'
          }`}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>
    </div>
  );
}

const CabDefinitionForm: React.FC<CabDefinitionFormProps> = ({
  formData,
  errors,
  onChange,
  onSubmit,
  onCancel,
  isSaving = false,
  nameReadOnly = false,
  showFooter = true,
}) => {
  const [lookupTarget, setLookupTarget] = useState<LookupTarget>(null);

  const openLookup = (target: LookupTarget) => setLookupTarget(target);
  const closeLookup = () => setLookupTarget(null);

  const handleUserConfirm = (users: LookupUser[]) => {
    if (lookupTarget === 'cabManager') {
      onChange({ cabManager: users[0] ?? null });
    } else if (lookupTarget === 'boardMembers') {
      onChange({ boardMembers: users });
    }
    closeLookup();
  };

  const handleGroupConfirm = (groups: LookupGroup[]) => {
    if (lookupTarget === 'delegates') {
      onChange({ delegates: groups[0] ?? null });
    } else if (lookupTarget === 'boardGroups') {
      onChange({ boardGroups: groups });
    }
    closeLookup();
  };

  const handleFormSubmit = (e: React.FormEvent, action: 'save' | 'saveContinue') => {
    e.preventDefault();
    onSubmit(action);
  };

  return (
    <>
      <form className="space-y-4" onSubmit={(e) => handleFormSubmit(e, 'save')} noValidate>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
          <div className="space-y-4">
            <FormTextField
              id="cab-name"
              label="Name"
              required
              value={formData.name}
              onChange={(value) => onChange({ name: value })}
              error={errors.name}
              maxLength={255}
              readOnly={nameReadOnly}
            />

            <LookupField
              id="cab-manager-lookup"
              label="CAB Manager"
              required
              mode="single"
              displayValue={formData.cabManager ? formatUserDisplayName(formData.cabManager) : undefined}
              onOpenLookup={() => openLookup('cabManager')}
              onClear={() => onChange({ cabManager: null })}
              error={errors.cabManager}
            />

            <LookupField
              id="delegates-lookup"
              label="Delegates"
              mode="single"
              displayValue={formData.delegates ? formatGroupDisplayName(formData.delegates) : undefined}
              onOpenLookup={() => openLookup('delegates')}
              onClear={() => onChange({ delegates: null })}
              placeholder="Select group"
            />

            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                <label htmlFor="cab-active" className="text-sm font-medium text-gray-700">
                  <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                  Active
                </label>
              </div>
              <div className="col-span-12 sm:col-span-8 lg:col-span-9 pt-2">
                <input
                  id="cab-active"
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => onChange({ active: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <FormSelectField
              id="cab-type"
              label="CAB Type"
              required
              value={formData.cabType}
              onChange={(value) => onChange({ cabType: value })}
              options={CAB_TYPE_FORM_OPTIONS}
              error={errors.cabType}
            />

            <LookupField
              id="board-members-lookup"
              label="Board Members"
              mode="multi"
              chipItems={formData.boardMembers.map((u) => ({
                id: u.id,
                label: formatUserDisplayName(u),
              }))}
              onOpenLookup={() => openLookup('boardMembers')}
              onRemoveChip={(id) =>
                onChange({ boardMembers: formData.boardMembers.filter((u) => u.id !== id) })
              }
              onClear={() => onChange({ boardMembers: [] })}
              placeholder="Select users"
            />

            <LookupField
              id="board-groups-lookup"
              label="Board Groups"
              mode="multi"
              chipItems={formData.boardGroups.map((g) => ({
                id: g.id,
                label: formatGroupDisplayName(g),
              }))}
              onOpenLookup={() => openLookup('boardGroups')}
              onRemoveChip={(id) =>
                onChange({ boardGroups: formData.boardGroups.filter((g) => g.id !== id) })
              }
              onClear={() => onChange({ boardGroups: [] })}
              placeholder="Select groups"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">
            <span className="text-red-500 mr-0.5" aria-hidden>*</span>
            Conference Details
          </h3>
          <p className="text-xs text-gray-500 -mt-2">
            Required. These details become the default conference information for all CAB Meetings
            generated from this definition.
          </p>

          <FormSelectField
            id="conference-type"
            label="Conference Type"
            required
            value={formData.conferenceType}
            onChange={(value) => onChange({ conferenceType: value })}
            options={CAB_CONFERENCE_TYPE_OPTIONS}
            error={errors.conferenceType}
          />

          <FormTextField
            id="meeting-subject"
            label="Meeting Subject / Title"
            value={formData.meetingSubject}
            onChange={(value) => onChange({ meetingSubject: value })}
            error={errors.meetingSubject}
            maxLength={255}
          />

          <FormTextField
            id="teams-meeting-url"
            label="Teams Meeting Link"
            required={/teams/i.test(formData.conferenceType)}
            value={formData.teamsMeetingUrl}
            onChange={(value) => onChange({ teamsMeetingUrl: value })}
            error={errors.teamsMeetingUrl}
          />

          <FormTextField
            id="teams-meeting-id"
            label="Meeting ID"
            value={formData.teamsMeetingId}
            onChange={(value) => onChange({ teamsMeetingId: value })}
            error={errors.teamsMeetingId}
          />

          <FormTextField
            id="teams-passcode"
            label="Passcode"
            value={formData.teamsPasscode}
            onChange={(value) => onChange({ teamsPasscode: value })}
            error={errors.teamsPasscode}
          />

          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
              <label htmlFor="conference-details" className="text-sm font-medium text-gray-700">
                Additional Notes
              </label>
            </div>
            <div className="col-span-12 sm:col-span-8 lg:col-span-9">
              <RichTextEditor
                value={formData.conferenceDetails}
                onChange={(value) => onChange({ conferenceDetails: value })}
                placeholder="Optional notes. A Teams invite body is generated automatically from the fields above."
                minHeight="120px"
              />
              {errors.conferenceDetails && (
                <p className="mt-1 text-xs text-red-600">{errors.conferenceDetails}</p>
              )}
            </div>
          </div>
        </div>

        {showFooter && (
        <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={(e) => handleFormSubmit(e, 'saveContinue')}
            className="px-4 py-2 text-sm border border-indigo-600 rounded-md text-indigo-700 bg-white hover:bg-indigo-50 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
        )}
      </form>

      <UserLookupModal
        isOpen={lookupTarget === 'cabManager' || lookupTarget === 'boardMembers'}
        onClose={closeLookup}
        mode={lookupTarget === 'boardMembers' ? 'multi' : 'single'}
        title="Users"
        initialSelected={
          lookupTarget === 'cabManager'
            ? formData.cabManager
              ? [formData.cabManager]
              : []
            : formData.boardMembers
        }
        onConfirm={handleUserConfirm}
      />

      <GroupLookupModal
        isOpen={lookupTarget === 'delegates' || lookupTarget === 'boardGroups'}
        onClose={closeLookup}
        mode={lookupTarget === 'boardGroups' ? 'multi' : 'single'}
        title="Groups"
        groupTypes={[...CAB_BOARD_GROUP_TYPES]}
        initialSelected={
          lookupTarget === 'delegates'
            ? formData.delegates
              ? [formData.delegates]
              : []
            : formData.boardGroups
        }
        onConfirm={handleGroupConfirm}
      />
    </>
  );
};

export default CabDefinitionForm;
